import {
  Component,
  EventEmitter,
  inject,
  Injector,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, mergeMap, shareReplay, tap } from 'rxjs/operators';
import {
  CrmControlValueAccessorDirective,
  crmControlValueAccessorFactory,
} from 'common-module/core';
import { Product } from '@app/core/model/product';
import { CrmSelectOption } from 'common-module/select';
import { ProductService } from '@app/core/products/product.service';
import { InputTagsComponent } from '@app/shared/components/input-tags/input-tags.component';
import { CrmPaginated } from 'common-module/table';
import get from 'lodash-es/get';
import { getCurrentCommercialYear } from '@app/shared/helpers/commecial-year.helper';
import { CrmMessageService } from 'common-module/message';

@Component({
  selector: 'app-plcard-product-input',
  template: `
    <div class="product-input">
      <h3 class="modal-label">{{ label }}</h3>
      <app-input-tags
        [label]="label"
        [placeholder]="placeholder"
        [autocompleteFunction]="onAutoComplete"
        [getTagsForValue]="getTagsForValue"
        (tagsChanged)="tagsChanged.emit($event)"
      ></app-input-tags>
    </div>
  `,
  standalone: true,
  imports: [InputTagsComponent],
  providers: [crmControlValueAccessorFactory(PlCardProductInputComponent)],
})
export class PlCardProductInputComponent
  extends CrmControlValueAccessorDirective
  implements OnInit
{
  @Input() public btnLabel?: string;
  @Input() public label?: string;
  @Input() public placeholder?: string;
  @Output()
  public tagsChanged = new EventEmitter<CrmSelectOption[]>();
  private commercialYear$!: Observable<Product | undefined>;
  private commercialYears!: Product[];
  private messageService = inject(CrmMessageService);

  constructor(
    inj: Injector,
    private productService: ProductService,
  ) {
    super(inj);
  }

  public override ngOnInit() {
    super.ngOnInit();
    this.setCommercialYearSub();
  }

  public onAutoComplete = (value: string) => {
    return this.searchProducts(value);
  };

  public getTagsForValue = (value: CrmSelectOption) => {
    return this.getItemsObs([value]);
  };

  private getItemsObs(event: CrmSelectOption[]) {
    return this.commercialYear$.pipe(
      mergeMap((cy) => {
        if (!cy) {
          return throwError(
            () => new Error('Error: missing commercial year in product search'),
          );
        }
        const index = this.commercialYears.findIndex((c) => c._id === cy._id);
        const [, ...futureYears] = this.commercialYears.slice(index);
        const params = {
          type: 'product',
          'meta.identifiers.commercialReference':
            event[0].data.meta.identifiers.commercialReference,
          '$or[0][meta.timing.commercialYear]': cy.meta.identifier,
          ...(futureYears?.length && {
            '$or[1][meta.timing.commercialYear]':
              futureYears?.[0]?.meta?.identifier,
          }),
          $limit: 1000,
        };
        return this.productService.listProducts(params);
      }),
      map(({ data }: CrmPaginated<Product>) =>
        data.map((p) => {
          const material = get(p, 'meta.identifiers.commercialReference');
          const planningName = get(p, 'meta.productNaming.bwPlanningName');
          const commercialYear = get(p, 'meta.timing.commercialYear');
          return {
            label: `${material} - ${planningName} (${commercialYear})`,
            value: p._id,
          };
        }),
      ),
    );
  }

  private searchProducts(code: string): Observable<CrmSelectOption[]> {
    if (!code) {
      return of([]);
    }
    const is77Code = code[0] === '7';
    return this.commercialYear$.pipe(
      mergeMap((cy) => {
        if (!cy) {
          this.messageService.error('plCards.errors.missingCommercialYear');
          return throwError(
            () => new Error('Error: missing commercial year in product search'),
          );
        }
        const index = this.commercialYears.findIndex((c) => c._id === cy._id);
        const [, ...futureYears] = this.commercialYears.slice(index);
        const params: any = {
          type: 'product',
          '$or[0][meta.timing.commercialYear]': cy.meta.identifier,
          ...(futureYears?.length && {
            '$or[1][meta.timing.commercialYear]':
              futureYears?.[0]?.meta?.identifier,
          }),
          $limit: 1000,
        };
        if (is77Code) {
          params['meta.identifiers.materialCode[$regex]'] = `^${code}`;
        } else {
          params['meta.identifiers.commercialReference[$options]'] = 'i';
          params['meta.identifiers.commercialReference[$regex]'] = `^${code}`;
        }
        return this.productService.listProducts(params);
      }),
      map(({ data }: CrmPaginated<Product>) =>
        data.map((p) => {
          const material = get(p, 'meta.identifiers.materialCode');
          const commercialReference = `${get(p, 'meta.identifiers.commercialReference')} (${material})`;
          const planningName = get(p, 'meta.productNaming.bwPlanningName');
          const commercialYear = get(p, 'meta.timing.commercialYear');
          return {
            label: `${is77Code ? material : commercialReference} - ${planningName} (${commercialYear})`,
            value: p._id,
            data: p,
          };
        }),
      ),
    );
  }

  private setCommercialYearSub() {
    this.commercialYear$ = this.productService
      .listCommercialYears({
        '$sort[meta.identifier]': 1,
      })
      .pipe(
        tap((cys) => (this.commercialYears = cys)),
        map((cys) => getCurrentCommercialYear(cys)),
        shareReplay(1),
      );
  }
}
